import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  Award, 
  CheckCircle2, 
  Download, 
  Printer, 
  Copy, 
  Check, 
  ExternalLink, 
  Send, 
  FileText, 
  Briefcase, 
  Building2, 
  Server, 
  Cpu, 
  KeyRound, 
  AlertTriangle,
  Globe,
  Mail,
  Phone
} from 'lucide-react';
import { MasterProfile } from '../types';

interface SecurityDossierProps {
  masterProfile: MasterProfile;
  onOpenPrintDossier: () => void;
}

export const SecurityDossier: React.FC<SecurityDossierProps> = ({
  masterProfile,
  onOpenPrintDossier
}) => {
  const [copiedLink, setCopiedLink] = useState(false);

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(window.location.origin);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="space-y-10 max-w-5xl mx-auto pb-16">
      
      {/* Dossier Top Banner */}
      <div className="relative rounded-3xl bg-gradient-to-r from-slate-900 via-slate-900 to-emerald-950/40 border border-slate-800 p-8 sm:p-10 shadow-2xl overflow-hidden">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center space-x-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>UK Cybersecurity & MSP Consultant Dossier</span>
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-500/10 text-sky-400 border border-sky-500/20">
                Outside IR35 / Contract Ready
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              {masterProfile.fullName}
            </h1>
            <p className="text-base sm:text-lg font-semibold text-emerald-400">
              {masterProfile.title}
            </p>

            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-300 pt-1">
              <span className="flex items-center space-x-1">
                <Mail className="w-3.5 h-3.5 text-slate-400" />
                <span>{masterProfile.email}</span>
              </span>
              <span>•</span>
              <span className="flex items-center space-x-1">
                <Phone className="w-3.5 h-3.5 text-slate-400" />
                <span>{masterProfile.phone}</span>
              </span>
              <span>•</span>
              <span className="flex items-center space-x-1">
                <Globe className="w-3.5 h-3.5 text-slate-400" />
                <span>{masterProfile.location}</span>
              </span>
            </div>
          </div>

          <div className="flex sm:flex-col gap-2 shrink-0">
            <button
              onClick={() => window.print()}
              className="px-4 py-2.5 rounded-xl font-bold text-xs bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/25 flex items-center justify-center space-x-2 transition-all"
            >
              <Printer className="w-4 h-4" />
              <span>Print Executive Dossier (PDF)</span>
            </button>

            <button
              onClick={handleCopyShareLink}
              className="px-4 py-2.5 rounded-xl font-semibold text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center space-x-2 transition-all"
            >
              {copiedLink ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              <span>{copiedLink ? 'Link Copied!' : 'Share Dossier URL'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Executive Summary Statement */}
      <div className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-3 shadow-xl">
        <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider flex items-center space-x-2">
          <ShieldCheck className="w-4 h-4" />
          <span>Executive Capability Statement</span>
        </h3>
        <p className="text-sm text-slate-200 leading-relaxed bg-slate-950/80 p-4 rounded-2xl border border-slate-800/80">
          {masterProfile.summary}
        </p>
      </div>

      {/* Core Technical & Security Competency Matrix */}
      <div className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-6 shadow-xl">
        <div>
          <h3 className="text-lg font-bold text-white">Security & Infrastructure Competency Matrix</h3>
          <p className="text-xs text-slate-400">Validated operational domains across UK MSPs and enterprise environments</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Domain 1 */}
          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
            <div className="flex items-center space-x-2 text-sm font-bold text-emerald-400">
              <Award className="w-4 h-4" />
              <span>Compliance, Auditing & Governance</span>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-300">
              <li className="flex items-start space-x-2">
                <span className="text-emerald-400">✓</span>
                <span><strong>Cyber Essentials & Cyber Essentials Plus</strong> readiness assessments</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-emerald-400">✓</span>
                <span><strong>ISO 27001 & NIST CSF</strong> control gap analysis and policy authoring</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-emerald-400">✓</span>
                <span>Third-party supply chain risk and vendor security audits</span>
              </li>
            </ul>
          </div>

          {/* Domain 2 */}
          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
            <div className="flex items-center space-x-2 text-sm font-bold text-sky-400">
              <Lock className="w-4 h-4" />
              <span>SOC Operations & SIEM Threat Detection</span>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-300">
              <li className="flex items-start space-x-2">
                <span className="text-sky-400">✓</span>
                <span><strong>Microsoft Sentinel & Splunk</strong> SIEM rule creation and log ingestion</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-sky-400">✓</span>
                <span>Alert triage, suspicious activity analysis, and ransomware containment</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-sky-400">✓</span>
                <span>Phishing simulation campaigns and user security awareness training</span>
              </li>
            </ul>
          </div>

          {/* Domain 3 */}
          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
            <div className="flex items-center space-x-2 text-sm font-bold text-amber-400">
              <Server className="w-4 h-4" />
              <span>Microsoft 365, Cloud & Identity Hardening</span>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-300">
              <li className="flex items-start space-x-2">
                <span className="text-amber-400">✓</span>
                <span><strong>Microsoft 365 & Entra ID (Azure AD)</strong> Conditional Access policies</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-amber-400">✓</span>
                <span>MFA enforcement, Privileged Identity Management (PIM) & Zero Trust</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-amber-400">✓</span>
                <span>Windows Server & Linux hardening, GPO configuration, Active Directory</span>
              </li>
            </ul>
          </div>

          {/* Domain 4 */}
          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
            <div className="flex items-center space-x-2 text-sm font-bold text-indigo-400">
              <AlertTriangle className="w-4 h-4" />
              <span>Vulnerability Management & MSP Support</span>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-300">
              <li className="flex items-start space-x-2">
                <span className="text-indigo-400">✓</span>
                <span><strong>Nessus, Qualys & OpenVAS</strong> automated vulnerability scanning</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-indigo-400">✓</span>
                <span><strong>EDR / Endpoint Security</strong> (Microsoft Defender, CrowdStrike)</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-indigo-400">✓</span>
                <span>Firewall configuration (Fortinet, Sophos, pfSense, Cisco Meraki)</span>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* Productized Engagement Models (For UK MSPs and Clients) */}
      <div className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-6 shadow-xl">
        <div>
          <h3 className="text-lg font-bold text-white">Consulting & Overflow Engagement Packages</h3>
          <p className="text-xs text-slate-400">Ready-to-deploy technical support for UK Managed Service Providers and businesses</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          
          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3 flex flex-col justify-between">
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Fast Start</span>
              <h4 className="text-sm font-bold text-white">MSP Security Overflow</h4>
              <div className="text-xl font-extrabold text-white">£350 - £450 <span className="text-xs font-normal text-slate-400">/ day</span></div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Clear client ticket backlogs, perform Cyber Essentials audits, and handle Tier 2/3 security escalations with zero onboarding lag.
              </p>
            </div>
            <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded text-center">
              Available 24-48h Notice
            </span>
          </div>

          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3 flex flex-col justify-between">
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-sky-400 uppercase tracking-wider">Fixed Deliverable</span>
              <h4 className="text-sm font-bold text-white">Cyber Essentials Audit Sprint</h4>
              <div className="text-xl font-extrabold text-white">£750 - £1,200 <span className="text-xs font-normal text-slate-400">/ project</span></div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Complete baseline vulnerability scan, boundary firewall check, device patching audit, and remediation roadmap.
              </p>
            </div>
            <span className="text-[10px] font-semibold text-sky-400 bg-sky-500/10 px-2 py-1 rounded text-center">
              2-Day Turnaround
            </span>
          </div>

          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3 flex flex-col justify-between">
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Cloud Security</span>
              <h4 className="text-sm font-bold text-white">M365 & Cloud Hardening</h4>
              <div className="text-xl font-extrabold text-white">£850 - £1,500 <span className="text-xs font-normal text-slate-400">/ tenant</span></div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Implement Microsoft 365 Entra ID Conditional Access, MFA, anti-phishing protection, and secure score elevation.
              </p>
            </div>
            <span className="text-[10px] font-semibold text-amber-400 bg-amber-500/10 px-2 py-1 rounded text-center">
              Full Tenant Hardening
            </span>
          </div>

        </div>
      </div>

      {/* Work History Summary */}
      <div className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-4 shadow-xl">
        <h3 className="text-lg font-bold text-white">Verified Career Highlights</h3>
        
        <div className="space-y-4">
          {masterProfile.experiences.map((exp) => (
            <div key={exp.id} className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <div>
                  <h4 className="text-sm font-bold text-white">{exp.role}</h4>
                  <span className="text-xs font-semibold text-emerald-400">{exp.company}</span>
                </div>
                <span className="text-xs text-slate-400">{exp.period} • {exp.location}</span>
              </div>
              <ul className="list-disc list-outside ml-4 space-y-1 text-xs text-slate-300 pt-1">
                {exp.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};
